import type { JSONContent } from "@tiptap/core";
import { PURCHASER_API_BASE_URL, getHeaders } from "../Constants";
import type { XOR } from "../../types.ts";
import { z } from "zod";
import type { RFQFilterParams } from "../../components/RFQ/List/useRFQListFilters.tsx";
import { objectToQueryParams } from "../../util/logic.ts";
import type { RFQReminder } from "./RFQReminders.ts";
import type { Label } from "./Labels.ts";
import type { User } from "./Auth.ts";
import type { FileUpload } from "./FileUpload.ts";
import type {
    RFQCommunication,
    RFQDraftCommunication,
    RFQMailboxContact,
    RFQUncategorizedCommunication,
    UpdateRFQCommunicationParams,
} from "./RFQMailbox.ts";

// Matches the statuses returned from the backend
export const RFQStatusSchema = z.enum(["IN_PROGRESS", "DRAFT", "CLOSED", "ARCHIVED"]);
export type RFQStatus = z.infer<typeof RFQStatusSchema>;

export enum RFQStatuses {
    IN_PROGRESS = "IN_PROGRESS",
    DRAFT = "DRAFT",
    CLOSED = "CLOSED",
    ARCHIVED = "ARCHIVED",
}

// Meant to replicate the Prisma schema type in purchaser-api/prisma/schema.prisma
export type RFQ = {
    id: number;
    uniquePublicId: string;
    createdAt: string;
    updatedAt: string; // RFQ database entity updatedAt
    deletedAt: string | null;
    archivedAt: string | null;
    title: string | null;
    content: string;
    contentJson: JSONContent;
    subject: string;
    requestSummary: string;
    submissionDeadline: string | null;
    rfqExtractionStartedAt: string | null; // ISO date string, set when RFQ line item extraction begins

    // Relationships
    teamId: number;
    // team
    createdByUserId: number;
    createdByUser: User;
    assignedToUserId: number;
    assignedToUser: User;
    senderId: number;
    sender: UserEmailSender;

    rfqAttachments: RFQAttachment[];
    rfqQuoteComparisons: RfqQuoteComparison[];
    rfqReminders: RFQReminder[];
    labels: Label[];

    // TODO: this is any right now because the final structure isn't decided upon
    comparisons: any[];
    rfqSubscriptions: RfqSubscription[];
    tables: RFQTabular[];

    // Computed by the backend
    status: RFQStatus;
    hasUnread: boolean;
    quoteCount: number;
    messageCount: number;
    vendorCount: number; // count of unique vendorRelationshipIds
    contacts: RFQMailboxContact[]; // Unique list of contacts for the RFQ

    lastUpdated: string; // last relevant change to anything related to the RFQ

    // Computed by Prisma
    emailAddress: string;
};

export type RFQListEntry = {
    uniquePublicId: string;
    createdAt: string;
    updatedAt: string;
    title: string | null;
    createdByUserDisplayName: string;
    createdByUserId: number;
    assignedToUserDisplayName: string;
    assignedToUserId: number;
    status: RFQStatus;
    hasUnread: boolean;
    quoteCount: number;
    messageCount: number;
    awardCount: number;

    vendorCount: number; // count of unique vendorRelationshipIds
};

// More than this is returned but seem to be the only fields used
type NewRFQResponse = {
    uniquePublicId: string;
    globalUniqueId: string;
    emailAddress: string;
    subject: string | null;
};

export type VendorRelationship = {
    id: number;
    createdAt: string;
    deletedAt: string | null;
    updatedAt: string;
    name: string | null;
    attributeValues: VendorAttributeValue[];
    contacts: { id: number; email: string; teamEmailAddressId: number }[]; // minimal Contact
    files?: VendorRelationshipFile[];
};

export type VendorRelationshipFile = {
    id: number;
    file: {
        id: number;
        originalFilename: string;
        internalFilename: string;
        fileSizeBytes: number;
        mimeType: string;
    };
};

export type Contact = {
    email: string;
    id: number;
    teamEmailAddressId: number;
    vendorName?: string | null;
    vendorRelationshipId: number;
    vendorRelationship: VendorRelationship;
    attributeValues: ContactAttributeValue[];
    lists: ShortContactList[];

    // Not used anywhere (yet)
    createdAt: string;
    createdBy: number;
    deletedAt: string | null;
    updatedAt: string;
};

export type ShortContactList = {
    id: number;
    name: string;
};

export enum AttributeType {
    STRING = "STRING",
    FILE = "FILE",
}

export type ContactAttributeValue = {
    attributeId: number;
    name: string;
    description: string;
    value: string;
};

export type VendorAttributeValue = {
    attributeId: number;
    name: string;
    description: string;
    value: string;
    type: AttributeType;
    // Only present if type is FILE
    file?: FileUpload;
    fileId?: number | null; // Only present if type is FILE
};

export type ContactAttribute = {
    id: number;
    type: AttributeType.STRING;
    name: string;
    description: string;
    contactCount: number;
    weight: number;
};

export type VendorAttribute = {
    id: number;
    type: AttributeType;
    name: string;
    description: string;
    vendorCount: number;
    weight: number;
};

export type RFQAttribute = {
    id: number;
    type: AttributeType.STRING;
    name: string;
    description: string;
    rfqCount: number;
    weight: number;
};

export type RFQAttributeValue = {
    attributeId: number;
    name: string;
    description: string;
    value: string;
    type: AttributeType.STRING;
};

export interface UserEmailSender {
    id: number;
    email?: string;
    emailEngineAccountId: string;
    createdAt: string;
    updatedAt: string;
    userId: string;
    teamId: string;
    // TODO: Needs type
    emailEngineData?: any;
}

export interface UserEmailSenderSignature extends UserEmailSender {
    signature: string;
}

export type RFQAttachment = {
    id: number;
    createdAt: string;
    updatedAt: string;
    fileUploadId: number;
    rfqId: number;
    file: FileUpload;
};

export type RfqQuote = {
    id: number;
    read: boolean;
    summary: string;
    grandTotal?: number;
    grandTotalMetadata?: {
        defined?: number; // Explicitly stated total
        inferred?: number; // Calculated total
        confidence: number; // 0-100
        calculationMethod?: string; // How inferred value was derived
    };
    currencyCode?: string;
    currencyCodeMetadata?: {
        defined?: string; // Explicitly stated currency
        inferred?: string; // Inferred from context
        confidence: number; // 0-100
    };
    validUntil?: string;
    validUntilMetadata?: {
        type: "ABSOLUTE" | "RELATIVE";
        originalText: string; // Original quote text (e.g., "valid for 30 days")
        confidence: number; // 0-100
        calculatedFrom?: string; // ISO timestamp used as reference (for RELATIVE dates)
    };
    leadTime?: string;
    leadTimeMetadata?: {
        confidence: number; // 0-100
        reasoning?: string; // Explanation of classification
    };
    quoteNumber?: string;
    quoteNumberMetadata?: {
        confidence: number; // 0-100
    };
    createdAt: string;
    emailAttachment: {
        file: FileUpload;
    };
};

export type RfqQuoteComparison = {
    id: number;
    title?: string;
    read: boolean;
    description?: string;
    rawOutput: string;
    tableContent: string;
    createdAt: string;
    mailboxId: number;
};

// purchaser-api/services/ai/RFQv2/GenerateRFQFromSingleFile.ts
type PatchRFQResult = {
    headers: string[];
    rows: string[][];
};

export type RFQListStats = {
    totalRFQs: number;
    statusCounts: {
        status: string;
        count: number;
    }[];
    userCreatedCounts: {
        userId: number;
        userDisplayName: string;
        rfqCount: number;
    }[];
    userAssignedCounts: {
        userId: number;
        userDisplayName: string;
        rfqCount: number;
    }[];
    dateRangeCounts: {
        today: { startTime: number; createdAtCount: number; updatedAtCount: number };
        week: { startTime: number; endTime: number; createdAtCount: number; updatedAtCount: number };
        month: { startTime: number; endTime: number; createdAtCount: number; updatedAtCount: number };
        ytd: { startTime: number; endTime: number; createdAtCount: number; updatedAtCount: number };
    };
};

export type RFQListResponse = {
    stats: RFQListStats;
    results: RFQListEntry[];
    pagination?: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
    };
};

export type RFQTabular = {
    id: number;
    rfqId: number;
    rfq: RFQ;
    tabularDataStoreId: number;
    tabularDataStore: Tabular;
};

export type MailboxTabular = {
    id: number;
    vendorName: string;
    mailboxId: number;
    communicationId: number;
    communication?: RFQCommunication;
    changedFields: string[];
    revision: number;
    table: Tabular;
    type: "LINE_ITEMS";
};

export type Tabular = {
    columnDefinitions: TabularColumnDefinition[];
    rows: TabularRow[];
    createdAt: string;
    updatedAt: string;
    description: string;
    id: number;
    name: string;
};

export type TabularColumnDefinition = {
    id: string;
    dataType: "text" | "number" | "date" | "currency";
    hidden: boolean;
    name: string;
    order: number;
};

export type TabularRow = {
    id: number;
    order: number;
    cells: TabularRowCell[];
};

export type TabularRowCell = {
    columnId: string;
    columnName: string;
    value: string | number | Date | Array<any>;
    dataType: "text" | "number" | "date" | "currency" | "object";
    hidden: boolean;
};

// Compliance Report Types
export type CheckStatus = "PASS" | "FAIL" | "SKIPPED";
export type ItemStatus = "PASS" | "WARNING" | "FAIL";
export type PrimaryIssue = "None" | "SPEC_DEVIATION" | "UOM_MISMATCH" | "QTY_VARIANCE" | "LOGISTICS_VARIANCE" | "NO_BID";

export type ComplianceCategories = {
    spec_check: CheckStatus;
    uom_check: CheckStatus;
    qty_check: "PASS" | "FAIL"; // qty_check cannot be SKIPPED
    logistics_check: CheckStatus;
};

export type ComplianceEvidence = {
    rfq_row_id: string;
    rfq_column_id: string;
    rfq_value: string;
    quote_value: string;
};

export type ComplianceItem = {
    rfq_id: string;
    status: ItemStatus;
    categories: ComplianceCategories;
    primary_issue: PrimaryIssue;
    confidence: number; // 0-100
    analysis: string;
    evidence: ComplianceEvidence;
};

export type ComplianceSummary = {
    total_lines_scanned: number;
    total_issues_found: number;
    severity_counts: {
        critical: number;
        warning: number;
    };
    category_counts: {
        spec_deviation: number;
        qty_variance: number;
        uom_mismatch: number;
        logistics_variance: number;
        no_bid: number;
    };
};

export type ComplianceReportData = {
    summary: ComplianceSummary;
    compliance_report: ComplianceItem[];
};

export type ComplianceStatus = "PENDING" | "PROCESSING" | "COMPLETED" | "FAILED";

export type ComplianceReport = {
    id: number;
    status: ComplianceStatus;
    reportData: ComplianceReportData;
    createdAt: string;
    errorMessage?: string;
    modelUsed?: string;
};

export type ListRecipient = {
    type: "LIST";
    id: number;
    contactCount: number;
    name: string;
    score: number; // float
};

export type ContactRecipient = {
    type: "CONTACT";
    id: number;
    email: string;
    teamEmailAddressId: number;
    vendorRelationshipId: number;
    vendorName: string | null;
    score: number; // float
};

export type RecipientItem = XOR<ListRecipient, ContactRecipient>;

export type RfqSubscription = {
    id: number;
    createdAt: string;
    updatedAt: string;
    userId: number;
    rfqId: number;
    user: User;
};

// From @types/routes/rfq/index.ts in purchaser-api
const RFQPatchSchema = z.object({
    title: z
        .string()
        .nullish()
        .transform(val => (val === null ? null : val?.trim() || undefined))
        .optional(),
    content: z.string().nullish(),
    contentJson: z.json().nullish(),
    subject: z
        .string()
        .nullish()
        .transform(val => (val === null ? null : val?.trim() || undefined))
        .optional(),
    senderId: z.number().nullish(),
    archive: z.boolean().nullable().optional(),
});

export type RFQPatch = z.infer<typeof RFQPatchSchema>;

export type VendorRfqCheckResult = {
    results: Record<string, { contactOnRfqs: string[]; vendorOnRfqs: string[] }>;
};

export interface BounceContactUpdate {
    mutation: "update" | "remove";
    contactId: number;
    email: string;
    originalEmail: string;
}

export default {
    getRFQList: async (filters?: RFQFilterParams & { page?: number; limit?: number }): Promise<RFQListResponse> => {
        const queryParams = filters ? objectToQueryParams(filters) : null;
        const queryString = queryParams ? "?" + queryParams.toString() : "";

        const url = PURCHASER_API_BASE_URL + "/v2/rfqs" + queryString;

        const res = await fetch(url, { headers: getHeaders() });
        if (!res.ok) {
            console.error("Failed to get RFQs", res);
            throw new Error("Failed to get RFQs");
        }
        return (await res.json()) as RFQListResponse;
    },

    getRFQv2: (publicId: string) =>
        fetch(`${PURCHASER_API_BASE_URL}/v2/rfqs/${publicId}`, { headers: getHeaders() }).then(res => res.json()) as Promise<RFQ>,

    sendRFQv2: (publicId: string, mailboxIds: number[]) =>
        fetch(`${PURCHASER_API_BASE_URL}/v2/rfqs/${publicId}/send`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify({ mailboxIds }),
        }).then(res => res.json()),

    updateRFQ: async (params: { uniquePublicId: string; payload: RFQPatch }) => {
        const url = `${PURCHASER_API_BASE_URL}/v2/rfqs/${params.uniquePublicId}`;
        const res = await fetch(url, { method: "PATCH", headers: getHeaders(), body: JSON.stringify(params.payload) });

        if (!res.ok || res.status > 399) {
            throw new Error(`Failed to update RFQ with response: ${JSON.stringify(res)}`);
        }
    },

    updateRFQStatus: (publicId: string, status: RFQStatus) => {
        return fetch(`${PURCHASER_API_BASE_URL}/v2/rfqs/${publicId}/change-status/${status}`, {
            method: "PUT",
            headers: getHeaders(),
            body: JSON.stringify({}),
        }).then(res => {
            if (!res.ok) {
                throw new Error("Failed to update RFQ status");
            }
            return res.json();
        });
    },

    archiveRFQv2: (publicId: string, archive: boolean) => {
        return fetch(`${PURCHASER_API_BASE_URL}/v2/rfqs/${publicId}`, {
            method: "PATCH",
            headers: getHeaders(),
            body: JSON.stringify({ archive }),
        }).then(res => {
            if (!res.ok) {
                throw new Error("Failed to update archive status on RFQ");
            }
            return res.json();
        });
    },

    createRFQv2: (title = "", contentJson = {}): Promise<NewRFQResponse> => {
        /**
         * This method will create a new RFQ
         */
        return fetch(`${PURCHASER_API_BASE_URL}/v2/rfqs`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify({
                title,
                content: "",
                contentJson,
            }),
        }).then(res => {
            if (!res.ok) {
                throw new Error("Failed to create RFQ");
            }
            return res.json();
        });
    },

    generateSubjectLine: (publicId: string) => {
        /**
         * This method will generate a subject line for the RFQ
         */
        return fetch(`${PURCHASER_API_BASE_URL}/v2/rfqs/${publicId}/generate-subject`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify({}),
        }).then(res => {
            if (!res.ok) {
                throw new Error("Failed to generate subject line for RFQ");
            }
            return res.json();
        });
    },

    getRFQAttributeValues: (
        publicId: string,
    ): Promise<{
        attributeValues: RFQAttributeValue[];
    }> => {
        /**
         * This method fetches all of the attribute values for a specific RFQ
         */
        return fetch(`${PURCHASER_API_BASE_URL}/v2/rfqs/${publicId}/attributes`, {
            method: "GET",
            headers: getHeaders(),
        }).then(res => {
            if (!res.ok) {
                throw new Error("Failed to fetch RFQ attribute values");
            }
            return res.json();
        });
    },

    /**
     * Updates one or more attribute values for a specific RFQ.
     * @param rfqUniquePublicId The unique public id of the RFQ
     * @param attributes Array of attribute objects: { attributeId, type, value }
     */
    updateRFQAttributeValue: (rfqUniquePublicId: string, attributes: { attributeId: number; type: AttributeType.STRING; value: string | null }[]) => {
        return fetch(`${PURCHASER_API_BASE_URL}/v2/rfqs/${rfqUniquePublicId}/attributes`, {
            method: "PUT",
            headers: getHeaders(),
            body: JSON.stringify(attributes),
        }).then(res => {
            if (!res.ok) {
                throw new Error("Failed to update RFQ attribute value");
            }
            return res.json();
        });
    },

    assignUserToRFQ: (publicId: string, userId: number | null) => {
        if (userId === undefined) throw new Error("Invalid userId");
        return fetch(`${PURCHASER_API_BASE_URL}/v2/rfqs/${publicId}/assign-user`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify({ userId }),
        }).then(async res => {
            if (!res.ok) {
                throw new Error("Failed to assign user to RFQ");
            }
            const json = await res.json();
            return json;
        });
    },

    // Set submission deadline for an RFQ.
    // null can be passed to remove the deadline.
    setRFQSubmissionDeadline: async (publicId: string, submissionDeadline: Date | null) => {
        const formattedDeadline = submissionDeadline ? submissionDeadline.toISOString() : null;
        return fetch(`${PURCHASER_API_BASE_URL}/v2/rfqs/${publicId}/set-submission-deadline`, {
            method: "PATCH",
            headers: getHeaders(),
            body: JSON.stringify({ submissionDeadline: formattedDeadline }),
        }).then(async res => {
            if (!res.ok) {
                throw new Error("Failed to set RFQ submission deadline");
            }
            const json = await res.json();
            return json;
        });
    },

    /**
     * Uses a file to create quick RFQ boilerplate data and optionally add the file as an attachment - must upload
     * file first and get a fileUploadId
     */
    patchRFQWithFile: async (rfqPublicId: string, fileUploadId: number, attachFile: boolean): Promise<PatchRFQResult> => {
        return await fetch(`${PURCHASER_API_BASE_URL}/v2/rfqs/from-document`, {
            method: "PATCH",
            headers: getHeaders(),
            body: JSON.stringify({ rfqPublicId, fileUploadId, attachFile }),
        }).then(async res => {
            if (!res.ok) {
                throw new Error("Failed to create RFQ from document");
            }
            const json = await res.json();
            return json as PatchRFQResult;
        });
    },

    createRFQFromTemplate: (templateId: number, title = "") =>
        fetch(`${PURCHASER_API_BASE_URL}/v2/rfqs/from-template`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify({
                templateId,
                title,
            }),
        }).then(async res => {
            if (!res.ok) {
                throw new Error("Failed to create RFQ from template");
            }
            const json = await res.json();
            return json;
        }),

    patchRFQFromTemplate: (rfqPublicId: string | number, templateId: number) =>
        fetch(`${PURCHASER_API_BASE_URL}/v2/rfqs/from-template`, {
            method: "PATCH",
            headers: getHeaders(),
            body: JSON.stringify({
                // The API requires a string, but the UI code makes it a number in some
                // instances. This alleviates the ambiguity.
                rfqPublicId: rfqPublicId.toString(),
                templateId,
            }),
        }).then(async res => {
            if (!res.ok) {
                throw new Error("Failed to patch RFQ from template");
            }
            const json = await res.json();
            return json;
        }),

    attachFilesToRFQ: (publicId: string, fileUploadIds: number[]) => {
        /**
         * This method will attach files to the RFQ
         */
        return fetch(`${PURCHASER_API_BASE_URL}/v2/rfqs/${publicId}/attachments`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify({ fileUploadIds }),
        }).then(res => {
            if (!res.ok) {
                throw new Error("Failed to attach files to RFQ");
            }
            return res.json();
        });
    },

    detachFilesFromRFQ: (publicId: string, fileUploadIds: number[]) => {
        /**
         * This method will detach files from the RFQ
         */
        return fetch(`${PURCHASER_API_BASE_URL}/v2/rfqs/${publicId}/attachments`, {
            method: "DELETE",
            headers: getHeaders(),
            body: JSON.stringify({ fileUploadIds }),
        }).then(res => {
            if (!res.ok) {
                throw new Error("Failed to detach files from RFQ");
            }
            return res.json();
        });
    },

    getAttachmentDownloadLink: (mailboxId: number, communicationId: number, attachmentId: number) => {
        /**
         * This method returns a url and expiration date for a specific mailbix and communication
         */
        return fetch(`${PURCHASER_API_BASE_URL}/mailboxes/${mailboxId}/communications/${communicationId}/attachments/${attachmentId}/download`, {
            headers: getHeaders(),
        }).then(res => {
            if (!res.ok) {
                console.error(`Attempt to generate attachment download link failed`, { mailboxId, communicationId, attachmentId });
                throw new Error("Unable to generate download link for attachment.");
            }

            return res.json();
        });
    },

    getUncategorizedAttachmentDownloadLink: (uncategorizedCommunicationId: number, attachmentId: number) => {
        const url = `${PURCHASER_API_BASE_URL}/uncategorized/${uncategorizedCommunicationId}/attachments/${attachmentId}/download`;
        return fetch(url, { headers: getHeaders() }).then(res => {
            if (!res.ok) {
                console.error(`Attempt to generate uncategorized attachment download link failed`, { uncategorizedCommunicationId, attachmentId });
                throw new Error("Unable to generate download link for attachment.");
            }

            return res.json();
        });
    },

    replyToMailbox: (mailboxId: number, draftCommunicationId: number) => {
        /**
         * This method will reply to a mailbox with the given content
         * of the draft.
         */
        return fetch(`${PURCHASER_API_BASE_URL}/mailboxes/${mailboxId}/communications`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify({ draftCommunicationId }),
        }).then(res => {
            if (!res.ok) {
                throw new Error("Failed to reply to RFQ mailbox");
            }
            return res.json();
        });
    },

    broadcastToAllMailboxes: (rfqPublicId: string, draftCommunicationId: number) => {
        /**
         * This method will broadcast a message to all mailboxes
         * for a given RFQ with the given content of the draft.
         */
        return fetch(`${PURCHASER_API_BASE_URL}/v2/rfqs/${rfqPublicId}/broadcast`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify({ draftCommunicationId }),
        }).then(res => {
            if (!res.ok) {
                throw new Error("Failed to reply to RFQ mailbox");
            }
            return res.json();
        });
    },

    getUncategorizedCommunications: async (rfqUniquePublicId: string): Promise<RFQUncategorizedCommunication[]> => {
        const res = await fetch(`${PURCHASER_API_BASE_URL}/uncategorized?rfqUniquePublicId=${rfqUniquePublicId}`, { headers: getHeaders() });
        const json = await res.json();
        return json;
    },

    associateUncategorizedCommunicationWithExistingMailbox: (id: number, mailboxId: number) => {
        /**
         * This method will associate a communication with a mailbox
         */
        return fetch(`${PURCHASER_API_BASE_URL}/uncategorized/${id}/mailbox`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify({ mailboxId }),
        }).then(res => {
            if (!res.ok) {
                throw new Error("Failed to associate communication with mailbox");
            }
            return res.json();
        });
    },

    associateUncategorizedCommunicationWithNewMailbox: (id: number, rfqUniquePublicId: string) => {
        /**
         * This method will associate a communication with a new mailbox
         */
        return fetch(`${PURCHASER_API_BASE_URL}/uncategorized/${id}/mailbox/new`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify({ rfqUniquePublicId }),
        }).then(res => {
            if (!res.ok) {
                throw new Error("Failed to associate communication with new mailbox");
            }
            return res.json();
        });
    },

    moveUncategorizedCommunicationToNewRFQ: (id: number, rfqUniquePublicId: string) => {
        /**
         * This method will associate a communication with a mailbox
         */
        return fetch(`${PURCHASER_API_BASE_URL}/uncategorized/${id}/move`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify({ rfqUniquePublicId }),
        }).then(res => {
            if (!res.ok) {
                throw new Error("Failed to associate communication with mailbox");
            }
            return res.json();
        });
    },

    deleteUncategorizedCommunication: (id: number) => {
        /**
         * This method will delete a communication
         */
        return fetch(`${PURCHASER_API_BASE_URL}/uncategorized/${id}`, {
            method: "DELETE",
            headers: getHeaders(),
            body: JSON.stringify({}),
        }).then(res => {
            if (!res.ok) {
                throw new Error("Failed to delete communication");
            }
        });
    },

    /**
     * Mark a communication as read.  If you do this on an outgoing message, the API will throw an error.
     * @param communicationId
     */
    setCommunicationRead: (communicationId: number) => {
        return fetch(`${PURCHASER_API_BASE_URL}/communications/${communicationId}/mark-as-read`, {
            headers: getHeaders(),
            method: "POST",
            body: JSON.stringify({}),
        }).then(res => {
            if (!res.ok) {
                throw new Error("Failed to mark communication as read");
            }
            return res.json();
        });
    },

    createDraftCommunication: (rfqUniquePublicId: string, contentJson: JSONContent, mailboxId?: number | null, senderId?: number) => {
        /**
         * This method will create a draft communication for an RFQ
         * and mailbox (optional)
         */
        return fetch(`${PURCHASER_API_BASE_URL}/draft-communications`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify({ rfqUniquePublicId, mailboxId, contentJson, senderId }),
        }).then(res => {
            // If we get a 409 Conflict that means the draft already exists
            if (res.status === 409) return;

            if (!res.ok) {
                throw new Error("Failed to create draft communication");
            }
            return res.json();
        });
    },

    getDraftCommunication: (id: number) => {
        /**
         * This method will fetch a draft communication
         */
        return fetch(`${PURCHASER_API_BASE_URL}/draft-communications/${id}`, {
            method: "GET",
            headers: getHeaders(),
        }).then(res => {
            if (!res.ok) {
                throw new Error("Failed to fetch draft communication");
            }
            return res.json();
        });
    },

    getDraftCommunicationByRfqMailbox: (rfqUniquePublicId: string, mailboxId?: number | null): Promise<RFQDraftCommunication | null> => {
        /**
         * This method will fetch a draft communication based on
         * the RFQ and mailbox (optional). If mailbox is not passed
         * in, that indicates a broadcast communication draft.
         */
        return fetch(
            `${PURCHASER_API_BASE_URL}/draft-communications?rfqUniquePublicId=${rfqUniquePublicId}${mailboxId ? `&mailboxId=${mailboxId}` : ""}`,
            {
                method: "GET",
                headers: getHeaders(),
            },
        ).then(res => {
            if (!res.ok) {
                return null;
            }
            return res.json();
        });
    },

    updateDraftCommunication: (params: UpdateRFQCommunicationParams) => {
        /**
         * This method will update the content of a draft communication
         */
        return fetch(`${PURCHASER_API_BASE_URL}/draft-communications/${params.id}`, {
            method: "PATCH",
            headers: getHeaders(),
            body: JSON.stringify(params),
        }).then(res => {
            if (!res.ok) {
                throw new Error("Failed to update draft communication");
            }
            return res.json();
        });
    },

    deleteDraftCommunication: (id: number) => {
        /**
         * This method will delete a draft communication
         */
        return fetch(`${PURCHASER_API_BASE_URL}/draft-communications/${id}`, {
            method: "DELETE",
            headers: getHeaders(),
            body: JSON.stringify({}),
        }).then(res => {
            if (!res.ok) {
                throw new Error("Failed to delete draft communication");
            }
            return res.json();
        });
    },

    addFilesToDraftCommunication: (id: number, fileIds: number[]) => {
        /**
         * This method will add uploaded files as attachments
         * to a draft communication
         */
        return fetch(`${PURCHASER_API_BASE_URL}/draft-communications/${id}/attachments`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify({ fileIds }),
        }).then(res => {
            if (!res.ok) {
                throw new Error("Failed to add files to draft communication");
            }
            return res.json();
        });
    },

    removeFileFromDraftCommunication: (id: number, attachmentId: number) => {
        /**
         * This method will delete an attachment on a draft communication
         */
        return fetch(`${PURCHASER_API_BASE_URL}/draft-communications/${id}/attachments/${attachmentId}`, {
            method: "DELETE",
            headers: getHeaders(),
            body: JSON.stringify({}),
        }).then(res => {
            if (!res.ok) {
                throw new Error("Failed to remove file from draft communication");
            }
            return res.json();
        });
    },

    getRfqTabularData: async (rfqPublicId: string) => {
        const res = await fetch(`${PURCHASER_API_BASE_URL}/v2/rfqs/${rfqPublicId}/tables`, {
            headers: getHeaders(),
            method: "GET",
        });
        if (!res.ok) throw new Error("Failed to get RFQ tabular data");
        return res.json() as Promise<{ tables: Tabular[] }>;
    },

    getMailboxTabularData: async (mailboxId: string, includeCommunication = false) => {
        const res = await fetch(`${PURCHASER_API_BASE_URL}/mailboxes/${mailboxId}/tables?includeCommunication=${includeCommunication}`, {
            headers: getHeaders(),
            method: "GET",
        });
        if (!res.ok) {
            throw new Error("Failed to get mailbox tabular data");
        }
        return res.json() as Promise<{ data: MailboxTabular[] }>;
    },

    // this is how you get the actual rows for a revision
    getMailboxTabularDataRevision: async (mailboxId: string, revision = "latest") => {
        const res = await fetch(`${PURCHASER_API_BASE_URL}/mailboxes/${mailboxId}/tables/revisions/${revision}`, {
            headers: getHeaders(),
            method: "GET",
        });
        if (!res.ok) {
            throw new Error("Failed to get mailbox tabular data");
        }
        return res.json() as Promise<MailboxTabular>;
    },

    updateTabularField: async (tabularId: number, rowId: number, columnId: string, value: any): Promise<{ failed: any[]; successful: any[] }> => {
        const res = await fetch(`${PURCHASER_API_BASE_URL}/tabular-data/${tabularId}/rows/edit`, {
            method: "PUT",
            headers: getHeaders(),
            body: JSON.stringify({
                rows: [
                    {
                        id: rowId,
                        cells: [{ columnId, value: value }],
                    },
                ],
            }),
        });
        if (!res.ok) {
            throw new Error("Failed to update tabular field");
        }
        return res.json();
    },

    updateTabularColumnDefinition: (tabularId: string, columnId: string, name: string, hidden: boolean) => {
        return fetch(`${PURCHASER_API_BASE_URL}/tabular-data/${tabularId}/column-definitions`, {
            method: "PUT",
            headers: getHeaders(),
            body: JSON.stringify([{ id: columnId, name, hidden }]),
        }).then(res => {
            if (!res.ok) {
                throw new Error("Failed to update tabular column definition");
            }
            return res.json();
        });
    },

    setRFQQuoteRead: (rfqQuoteId: number) => {
        return fetch(`${PURCHASER_API_BASE_URL}/rfq-quotes/${rfqQuoteId}/mark-as-read`, {
            headers: getHeaders(),
            method: "POST",
            body: JSON.stringify({}),
        }).then(res => {
            if (!res.ok) {
                throw new Error("Failed to mark communication as read");
            }
            return res.json();
        });
    },

    deleteTabularRows: async (tabularId: number, rowIds: number[]): Promise<void> => {
        const res = await fetch(`${PURCHASER_API_BASE_URL}/tabular-data/${tabularId}/rows`, {
            method: "DELETE",
            headers: getHeaders(),
            body: JSON.stringify({ rowIds }),
        });
        if (!res.ok) {
            throw new Error("Failed to delete tabular rows");
        }
    },

    setRFQComparisonRead: (quoteComparisonId: number) => {
        return fetch(`${PURCHASER_API_BASE_URL}/rfq-quote-comparisons/${quoteComparisonId}/mark-as-read`, {
            headers: getHeaders(),
            method: "POST",
            body: JSON.stringify({}),
        }).then(res => {
            if (!res.ok) {
                throw new Error("Failed to mark communication as read");
            }
            return res.json();
        });
    },

    getRecipientOptions: async ({ search, limit, offset }: { search?: string; limit?: number; offset?: number }) => {
        let params: string[] = [];
        if (search) params.push(`search=${encodeURIComponent(search)}`);
        if (limit) params.push(`limit=${limit}`);
        if (offset) params.push(`offset=${offset}`);
        const res = await fetch(`${PURCHASER_API_BASE_URL}/v2/rfqs/recipients${params.length > 0 && "?" + params.join("&")}`, {
            method: "GET",
            headers: getHeaders(),
        });
        if (!res.ok) {
            throw new Error("Failed to get recipient options");
        }
        return (await res.json()) as Promise<{ recipients: RecipientItem[] }>;
    },

    resendAfterBounce: (mailboxId: number, communicationId: number, contactUpdates: BounceContactUpdate[]) => {
        return fetch(`${PURCHASER_API_BASE_URL}/mailboxes/${mailboxId}/communications/${communicationId}/resend-after-bounce`, {
            headers: getHeaders(),
            method: "POST",
            body: JSON.stringify({ contactUpdates }),
        }).then(res => {
            if (!res.ok) {
                throw new Error("Failed to resend communication after bounce");
            }
            return res.json();
        });
    },

    /**
     * Get the compliance report for a mailbox.
     * Returns null if no compliance report exists.
     * @param mailboxId - The mailbox ID
     * @param mailboxTableId - Optional mailbox table ID to get compliance for a specific revision
     */
    getMailboxComplianceReport: async (mailboxId: string, mailboxTableId?: number): Promise<ComplianceReport | null> => {
        const params = mailboxTableId ? `?mailboxTableId=${mailboxTableId}` : "";
        const res = await fetch(`${PURCHASER_API_BASE_URL}/mailboxes/${mailboxId}/compliance${params}`, {
            method: "GET",
            headers: getHeaders(),
        });
        if (res.status === 404) {
            return null;
        }
        if (!res.ok) {
            throw new Error("Failed to get compliance report");
        }
        return res.json();
    },

    /**
     * Trigger generation of a compliance report for a mailbox.
     * Returns the pending compliance record.
     */
    generateMailboxComplianceReport: async (mailboxId: string): Promise<ComplianceReport> => {
        const res = await fetch(`${PURCHASER_API_BASE_URL}/mailboxes/${mailboxId}/compliance/generate`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify({}),
        });
        if (!res.ok) {
            throw new Error("Failed to generate compliance report");
        }
        return res.json();
    },

    checkVendorOnRfqs: async (emails: string[]): Promise<VendorRfqCheckResult> => {
        const url = `${PURCHASER_API_BASE_URL}/v2/rfqs/vendor-check?emails=${encodeURIComponent(emails.join(","))}`;
        const res = await fetch(url, { headers: getHeaders() });
        if (!res.ok) throw new Error("Failed to check vendor RFQ associations");
        return await res.json();
    },

    /**
     * Export quote view data (Side by Side or Compliance) as Excel or CSV
     * @param publicId - The RFQ unique public ID
     * @param viewType - The view type: "side-by-side" or "compliance"
     * @param format - The export format: "xlsx" or "csv"
     * @param vendorColumns - Optional vendor column configuration for side-by-side view
     */
    exportQuoteView: async (
        publicId: string,
        viewType: "side-by-side" | "compliance",
        format: "xlsx" | "csv" = "xlsx",
        vendorColumns: { mailboxId: number; mailboxTableId: number; vendorName: string; activeColumnId: string; activeColumnName: string }[],
    ): Promise<Blob> => {
        const url = `${PURCHASER_API_BASE_URL}/v2/rfqs/${publicId}/export?view=${viewType}&format=${format}&vendorColumns=${encodeURIComponent(JSON.stringify(vendorColumns))}`;

        const res = await fetch(url, { headers: getHeaders() });
        if (!res.ok) {
            throw new Error("Failed to export quote view");
        }
        return await res.blob();
    },

    /**
     * Export a full multi-sheet RFQ workbook (Overview, RFQ Items, Side by Side, List View, Compliance, per-vendor sheets)
     * @param publicId - The RFQ unique public ID
     */
    exportFullRfqWorkbook: async (publicId: string): Promise<Blob> => {
        const url = `${PURCHASER_API_BASE_URL}/v2/rfqs/${publicId}/export?view=full-workbook&format=xlsx`;
        const res = await fetch(url, { headers: getHeaders() });
        if (!res.ok) throw new Error("Failed to export full RFQ workbook");
        return await res.blob();
    },

    /**
     * Export mailbox-specific quote view data (Side by Side or Compliance) as Excel or CSV
     * @param mailboxId - The mailbox ID
     * @param viewType - The view type: "side-by-side" or "compliance"
     * @param format - The export format: "xlsx" or "csv"
     * @param mailboxTableId - The specific revision's table ID to export
     */
    exportMailboxQuoteView: async (
        mailboxId: string | number,
        viewType: "side-by-side" | "compliance",
        format: "xlsx" | "csv" = "xlsx",
        mailboxTableId: number,
    ): Promise<Blob> => {
        const url = `${PURCHASER_API_BASE_URL}/mailboxes/${mailboxId}/export?view=${viewType}&format=${format}&mailboxTableId=${mailboxTableId}`;

        const res = await fetch(url, { headers: getHeaders() });
        if (!res.ok) {
            throw new Error("Failed to export mailbox quote view");
        }
        return await res.blob();
    },
};
